// Solver Diagnostics — PSD §2.5 (F2.5). Results diagnostics accordion content.

import type { CapTable, SolverDiagnostics as SolverDiagnosticsType, MultiAnchorPoint } from '@backsolve/shared-types';
import { downRoundDiagnosis } from '@backsolve/shared-logic/downRound';
import { adjustConversionRatio } from '@backsolve/shared-logic/antiDilution';
import { usd } from '../../format/format';
import { Tooltip } from '../common/Tooltip';

interface SolverDiagnosticsProps {
  diagnostics: SolverDiagnosticsType;
  capTable?: CapTable;
}

function Label({ label, help }: { label: string; help: string }) {
  return (
    <span className="metric-label">
      {label}
      <Tooltip label={help} ariaLabel={`Help: ${label}`} />
    </span>
  );
}

export function SolverDiagnostics({ diagnostics, capTable }: SolverDiagnosticsProps) {
  const { converged, iterations, residualError, tolerance, lowerBound, upperBound, multiAnchorCheck } =
    diagnostics;

  const downRound =
    multiAnchorCheck && multiAnchorCheck.length > 1 && capTable
      ? downRoundDiagnosis(multiAnchorCheck, capTable)
      : null;

  const earlier = downRound?.earlierInstrumentId
    ? capTable?.instruments.find((i) => i.id === downRound.earlierInstrumentId)
    : undefined;
  const later = downRound?.laterInstrumentId
    ? capTable?.instruments.find((i) => i.id === downRound.laterInstrumentId)
    : undefined;

  return (
    <div>
      <p className="result-caption" style={{ marginTop: 0 }}>
        Solver iterations fit the total equity value V so the implied per-share
        value of the anchor class matches the observed transaction price. A
        non-converged result is not reliable.
      </p>
      <div className="headline-grid">
        <div className="metric">
          <Label
            label="Converged"
            help="Whether the solver closed on a V within tolerance. If not, the FMV / strike are not meaningful — review the anchor and cap table."
          />
          <span className={`metric-value badge-${converged ? 'success' : 'error'}`}>
            {converged ? 'Yes' : 'No'}
          </span>
        </div>
        <div className="metric">
          <Label
            label="Iterations"
            help="Number of bracketing iterations the bisection-style solver ran to converge. Capped at solverMaxIterations."
          />
          <span className="metric-value">{iterations}</span>
        </div>
        <div className="metric">
          <Label
            label="Residual Error"
            help="Absolute relative gap between the anchor's implied and observed per-share price at the solved V. Should fall below tolerance to converge."
          />
          <span className="metric-value">{residualError.toFixed(6)}</span>
        </div>
        <div className="metric">
          <Label
            label="Tolerance"
            help="Convergence threshold on the residual (default 0.005 = 0.5%). Set per valuation via solverTolerance."
          />
          <span className="metric-value">{tolerance.toFixed(6)}</span>
        </div>
        <div className="metric">
          <Label
            label="Bracket Bounds"
            help="Final [lower, upper] equity-value bracket the solver narrowed. The solved V lies inside this range."
          />
          <span className="metric-value" style={{ fontSize: 'var(--font-size-2)' }}>
            {usd(lowerBound)} – {usd(upperBound)}
          </span>
        </div>
        <div className="metric">
          <Label
            label="Tolerance Sensitivity"
            help="Approximate per-share FMV uncertainty implied by the tolerance. Lower tolerance = tighter strike but more iterations."
          />
          <span className="metric-value" style={{ fontSize: 'var(--font-size-2)' }}>
            ±{(tolerance * 100).toFixed(2)}% per share
          </span>
        </div>
      </div>

      {multiAnchorCheck && multiAnchorCheck.length > 1 && (
        <div className="mt-4">
          <h4>Multi-Anchor Cross-Check</h4>
          <p className="result-caption">
            Re-derives V from every preferred class using the same waterfall.
            Deltas above 10% suggest inconsistent priced-round assumptions.
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Instrument</th>
                  <th>Implied Equity Value</th>
                  <th>Delta %</th>
                </tr>
              </thead>
              <tbody>
                {multiAnchorCheck.map((p: MultiAnchorPoint, i: number) => (
                  <tr key={i}>
                    <td>{p.instrumentId}</td>
                    <td>{usd(p.impliedEquityValue)}</td>
                    <td className={Math.abs(p.deltaPct) > 0.1 ? 'badge-warning' : ''}>
                      {(p.deltaPct * 100).toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {downRound && downRound.isDownRound && earlier && later && (
            <div className="mt-3">
              <p className="badge badge-warning">{downRound.note}</p>
              <details>
                <summary>Anti-dilution adjustment preview (broad-based weighted average)</summary>
                <p className="result-caption">
                  Read-only preview. Each preferred class&rsquo;s conversion ratio is
                  re-derived assuming a broad-based weighted-average adjustment
                  between the earlier and later rounds. The canonical valuation
                  is not modified.
                </p>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Class</th>
                        <th>Base Ratio</th>
                        <th>Adjusted Ratio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {capTable?.instruments
                        .filter((inst) => inst.kind === 'preferred')
                        .map((inst) => {
                          const round = {
                            oldPrice: earlier.issuePrice > 0 ? earlier.issuePrice : 1,
                            newPrice: later.issuePrice > 0 ? later.issuePrice : 1,
                            newShares: Math.max(1, later.sharesOutstanding),
                            oldShares: Math.max(1, earlier.sharesOutstanding),
                          };
                          const adjusted = adjustConversionRatio(inst, round, 'broadWeightedAverage');
                          const base = inst.conversionRatio && inst.conversionRatio > 0 ? inst.conversionRatio : 1;
                          return (
                            <tr key={inst.id}>
                              <td>{inst.name}</td>
                              <td>{base.toFixed(4)}</td>
                              <td>{adjusted.toFixed(4)}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
